import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AdditionalMenu } from '../../AdditionalMenu';
import { Tooltip } from '../../Tooltip/Tooltip';
import MuiSwitch from '@mui/material/Switch';
import RevertIcon from 'assets/images/revert.svg';
import { useTranslation } from 'react-i18next';
import { TableSettingsItem } from './TableSettingsItem';
import {
  ColumnConfiguration,
  TableVariant,
} from '../../Sim/Inventory/constants/columnConfigurationsConstants';
import { TABLE_VARIANT } from 'src/common/components/Sim/Inventory/constants/columnConfigurationsConstants';
import { ScrollArea } from '../../ScrollArea/ScrollArea';
import { pxToRem } from 'src/common/utils/fonts';

type TableSettingsProps = {
  columnConfig: ColumnConfiguration[];
  handleWidthChange: (columnName: string, width: number | 'auto', isCustomField: boolean) => void;
  handleVariantChange: (isChecked: boolean) => void;
  handleRevert: () => void;
  handleDrop: (oldIndex: number, newIndex: number) => void;
  variant: TableVariant;
  idLabelMap: Record<string, string>;
};

export const TableSettings: React.FC<TableSettingsProps> = ({
  columnConfig,
  handleWidthChange,
  handleVariantChange,
  handleRevert,
  handleDrop,
  variant,
  idLabelMap,
}) => {
  const { t } = useTranslation();
  return (
    <Box data-testid="configurable-table-settings-button">
      <AdditionalMenu>
        {({ onClose }) => (
          <Box
            className="table-menu-box"
            sx={{ width: pxToRem(256) }}
            data-testid="configurable-table-settings-popup"
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <ScrollArea style={{ maxHeight: pxToRem(755), flexBasis: '80vh' }}>
                {columnConfig.map((column, index) => {
                  return (
                    <React.Fragment key={column.name + index}>
                      <TableSettingsItem
                        index={index}
                        column={column}
                        handleWidthChange={handleWidthChange}
                        handleDrop={handleDrop}
                        variant={variant}
                        idLabelMap={idLabelMap}
                      />
                    </React.Fragment>
                  );
                })}
              </ScrollArea>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 1fr',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0,
                  px: 4,
                  '& .revert-icon:hover': { backgroundColor: 'rgba(23,118,218,0.04)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="text1"
                    sx={{
                      mt: 2,
                    }}
                  >
                    {t('common.combinedRows')}
                  </Typography>
                  <MuiSwitch
                    onChange={(e) => {
                      handleVariantChange(e.target.checked);
                      onClose();
                    }}
                    checked={variant === TABLE_VARIANT.Combined}
                    data-testid="configurable-table-settings-variant-switch"
                  />
                </Box>
                <Box
                  className="revert-icon"
                  sx={{
                    width: pxToRem(30),
                    height: pxToRem(30),
                    borderRadius: pxToRem(50),
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    justifySelf: 'end',
                  }}
                >
                  <Tooltip title={t('common.revertToDefault')}>
                    <RevertIcon
                      style={{ cursor: 'pointer' }}
                      width={pxToRem(21)}
                      height={pxToRem(18)}
                      role="img"
                      data-testid="revert-icon"
                      onClick={() => {
                        handleRevert();
                        onClose();
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </AdditionalMenu>
    </Box>
  );
};
